/* eslint-disable react-hooks/exhaustive-deps */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Flex, Stack, Badge, Menu, Button, Text } from '@chakra-ui/react';

import {
  CheckCircle,
  Clock,
  DotsThreeVertical,
  HourglassHigh,
  XCircle,
  CalendarBlank
} from '@phosphor-icons/react';

import {
  ScreeningStatus,
  ScreeningType,
  getScreeningStatusText,
  getScreeningTypeColor,
  getScreeningTypeLabel
} from '@/types/enums';
import { Screening } from '@/types/Screening';
import { formatDate, getCandidateFullName } from '@/utils';
import { apiCancelScreening, apiResendScreening } from '@/api/services';
import { fetchAPI } from '@/api/fetch';
import { toaster } from '@/components/ui/toaster';
import DeleteDialog from '@/components/dialogs/DeleteDialog';

interface ScreeningCardProps {
  screening: Screening;
  onUpdateScreening: (updatedScreening: Screening, removed: boolean) => void;
}

export default function ScreeningCard({ screening, onUpdateScreening }: ScreeningCardProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const allowResend = screening.status === ScreeningStatus.Scheduled;
  const allowEdit = [ScreeningStatus.Scheduled, ScreeningStatus.Expired].includes(screening.status);
  const allowCancel = [ScreeningStatus.Scheduled, ScreeningStatus.Expired].includes(
    screening.status
  );
  const showActions = allowResend || allowEdit || allowCancel;

  const getStatusIcon = (status: ScreeningStatus) => {
    switch (status) {
      case ScreeningStatus.AnalysisCompleted:
        return <CheckCircle size={20} />;
      case ScreeningStatus.InProgress:
      case ScreeningStatus.AnalysisTriggered:
      case ScreeningStatus.Completed:
        return <HourglassHigh size={20} />;
      case ScreeningStatus.Expired:
        return <XCircle size={20} />;
      case ScreeningStatus.Scheduled:
        return <Clock size={20} />;
      default:
        return <Clock size={20} />;
    }
  };

  const getStatusColor = (status: ScreeningStatus) => {
    switch (status) {
      case ScreeningStatus.Scheduled:
        return 'neutrals.500';
      case ScreeningStatus.Completed:
      case ScreeningStatus.InProgress:
      case ScreeningStatus.AnalysisTriggered:
        return 'warning.700';
      case ScreeningStatus.AnalysisCompleted:
        return 'success.700';
      case ScreeningStatus.Expired:
        return 'error.500';
      default:
        return 'neutrals.500';
    }
  };

  const getStatusBgColor = (status: ScreeningStatus) => {
    switch (status) {
      case ScreeningStatus.Scheduled:
        return 'neutrals.100';
      case ScreeningStatus.Completed:
      case ScreeningStatus.InProgress:
      case ScreeningStatus.AnalysisTriggered:
        return 'warning.100';
      case ScreeningStatus.AnalysisCompleted:
        return 'success.100';
      default:
        return 'neutrals.00';
    }
  };

  const showErrorToast = useCallback((message: string) => {
    toaster.error({
      title: 'Error',
      description: message,
      duration: 2000
    });
  }, []);

  const handleClick = () => {
    if (screening.screening_type === ScreeningType.JobInterview) {
      navigate(`/job/details/${screening.job_id}/candidate/${screening.candidate_id}`);
    } else {
      navigate(`/candidate/${screening.candidate_id}`);
    }
  };

  const handleResend = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    try {
      const response = await fetchAPI<void>({
        request: () => apiResendScreening(screening.screening_id)
      });

      if (!response.success) {
        console.error('Error resending screening:', response.message);
        showErrorToast('Failed to resend screening. Please try again.');
      }
    } catch (error) {
      console.error('Error updating screening:', error);
      showErrorToast('Failed to resend screening. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [screening.screening_id]);

  const handleCancelConfirm = useCallback(async () => {
    const response = await fetchAPI<void>({
      request: () => apiCancelScreening(screening.screening_id)
    });

    if (response.success) {
      onUpdateScreening(screening, true);
    } else {
      throw new Error(response.message || 'Failed to cancel screening');
    }
  }, [screening, onUpdateScreening]);

  return (
    <>
      <Box
        w="100%"
        border="1px solid"
        borderColor="neutrals.200"
        borderRadius="12px"
        p="20px"
        bg="background.primary"
        cursor="pointer"
        _hover={{
          boxShadow: 'md'
        }}
        transition="all 0.2s ease-in-out"
        onClick={handleClick}
      >
        <Flex justify="space-between" align="flex-start" gap="4px" h="100%">
          <Stack gap="8px" flex="1">
            {screening.screening_type === ScreeningType.JobInterview && (
              <Text fontSize="14px" fontWeight="semibold" color="neutrals.500">
                {screening.job?.client.name || 'Unknown Client'} -{' '}
                {screening.job?.job_title || 'Unknown Position'}
              </Text>
            )}

            <Text fontSize="18px" color="neutrals.900">
              {screening.candidate
                ? getCandidateFullName(
                    screening.candidate.first_name,
                    screening.candidate.last_name
                  )
                : 'Unknown Candidate'}{' '}
              - {screening.session_name}
            </Text>
            <Text fontSize="12px" color="neutrals.700">
              <Flex gap="8px" align="center" fontSize="14px">
                <CalendarBlank size={20} color="#6B7280" aria-hidden="true" />
                {formatDate(new Date(screening.deadline * 1000))}
              </Flex>
            </Text>
          </Stack>
          <Stack justify="space-between" align="flex-end" gap="8px" h="100%">
            <Flex align="center" gap="8px">
              <Badge
                bg={getStatusBgColor(screening.status)}
                border="1px solid"
                borderColor={getStatusColor(screening.status)}
                borderRadius="8px"
                p="2px 4px"
              >
                <Text fontSize="14px" color={getStatusColor(screening.status)} fontWeight="medium">
                  <Flex align="center" gap="4px">
                    {getStatusIcon(screening.status)}
                    {getScreeningStatusText(screening.status)}
                  </Flex>
                </Text>
              </Badge>
              {showActions && (
                <Menu.Root positioning={{ placement: 'bottom-start' }}>
                  <Menu.Trigger asChild>
                    <Button
                      variant="ghost"
                      color="neutrals.700"
                      borderRadius="full"
                      w="fit-content"
                      h="fit-content"
                      p="0"
                      _hover={{ bg: 'neutrals.100' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DotsThreeVertical size={24} weight="bold" />
                    </Button>
                  </Menu.Trigger>
                  <Menu.Positioner zIndex={1000}>
                    <Menu.Content borderRadius="8px">
                      {allowEdit && (
                        <Menu.Item
                          value="edit"
                          fontSize="16px"
                          cursor="pointer"
                          color="neutrals.900"
                          disabled={loading}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/interview/agent/update/${screening.screening_id}`);
                          }}
                        >
                          <Flex align="center" gap="8px">
                            Edit
                          </Flex>
                        </Menu.Item>
                      )}
                      {allowResend && (
                        <Menu.Item
                          value="resend"
                          fontSize="16px"
                          cursor="pointer"
                          color="neutrals.900"
                          disabled={loading}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResend();
                          }}
                        >
                          <Flex align="center" gap="8px">
                            Resend
                          </Flex>
                        </Menu.Item>
                      )}
                      {allowCancel && (
                        <Menu.Item
                          value="cancel"
                          fontSize="16px"
                          cursor="pointer"
                          color="error.500"
                          disabled={loading}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsCancelDialogOpen(true);
                          }}
                        >
                          <Flex align="center" gap="8px">
                            Cancel
                          </Flex>
                        </Menu.Item>
                      )}
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
              )}
            </Flex>
            <Flex flex="1" />
            <Badge
              mt="auto"
              borderRadius="12px"
              bg={getScreeningTypeColor(screening.screening_type)}
              p="2px 6px"
            >
              <Text fontSize="12px" color="neutrals.700">
                {getScreeningTypeLabel(screening.screening_type)}
              </Text>
            </Badge>
          </Stack>
        </Flex>
      </Box>

      <DeleteDialog
        isOpen={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        title="Cancel Interview"
        itemName={screening.session_name}
        itemType="screening"
        onConfirm={handleCancelConfirm}
        confirmLabel="Confirm"
        cancelLabel="Close"
      />
    </>
  );
}
